import './App.css';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import TagManager from 'react-gtm-module'
import routes from "./routes.json";
import { isUrl, _eval } from "./utils";
import { hook } from "./hooks";
import TextInput from "./form-components/TextInput"
import TextareaInput from "./form-components/TextAreaInput"
import DateInput from "./form-components/DateInput"
import FileInput from "./form-components/FileInput"
import CheckboxInput from "./form-components/CheckboxInput"
import CheckboxesInput from "./form-components/CheckboxesInput"
import SelectInput from "./form-components/SelectInput"
import RadioInput from "./form-components/RadioInput"
import SuccessModal from "./form-components/SuccessModal"
import ErrorModal from "./form-components/ErrorModal"

import { useParams } from 'react-router';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Spinner from 'react-bootstrap/Spinner';


function App(params) {

    const _config = (params.config) ? params.config : {}

    const [ready, setReady] = useState(false);
    const [config, setConfig] = useState(_config);
    const [formData, setFormData] = useState({});
    const [validated, setValidated] = useState(false);
    const [formClosed, setFormClosed] = useState(false);
    const [pendingSend, setPendingSend] = useState(false);
    const [pendingHooks, setPendingHooks] = useState(false);
    const [errorModalShow, setErrorModalShow] = useState(false);
    const [successModalShow, setSuccessModalShow] = useState(false);
    const [errorsBeforeSubmit, setErrorsBeforeSubmit] = useState(false);
    const [steps, setSteps] = useState({ min: 0, max: 0, current: 0 });


    /*
     *
     * Get config data from json file 
     */

    const { slug } = useParams();
    const confFile = routes.find(r => r.slug === slug).conf
    const getConfig = async () => {

        // get config from json file
        const response = await fetch(`${process.env.PUBLIC_URL}/config/${confFile}.json`)
        let conf = await response.json();

        // override config with url params
        const urlParams = new URLSearchParams(window.location.search);
        function overrideConfWithUrlParams(iterator) {
            let next;
            while ((next = iterator.next()) && !next.done) {
                let key = next.value
                let val = urlParams.get(next.value)

                // convert string to number
                if (!isNaN(val)) val = Number(val)

                // convert string to boolean
                if (val === 'true') val = true
                if (val === 'false') val = false

                // auto update field by URL param
                const field = conf.inputs.find(i => i.sid === key)
                if (field !== undefined) {
                    handleChange(key, { value: val, validity: true })
                    field.value = val
                }

                if (key === 'mtm_campaign')
                    formData['MTM_CAMPAIGN'] = { value: val };
                else
                    conf[key] = val
            }
        }
        overrideConfWithUrlParams(urlParams.keys())

        // Inject google tag manager if needed
        if (_eval(conf, 'googleTagManagerId'))
            TagManager.initialize({
                gtmId: conf.googleTagManagerId
            })


        // Run hook if any 
        if (_eval(conf, 'hook')) {
            setPendingHooks(true);
            conf = await hook(conf.hook, conf);
            setPendingHooks(false);
        }
        
        // set state
        setConfig(conf);

        // set steps data
        getFormSteps(conf);
    }

    // get config on mount
    useEffect(() => {
        if (config && Object.keys(config).length === 0)
            getConfig()
    })

    // wait for config to be set and then set ready to true
    useEffect(() => {
        if (config && Object.keys(config).length > 0)
            setReady(true)
    }, [config])

    // in build mode, update config on change 
    useEffect(() => {
        setConfig(params.config)
    }, [params.config])


    /*
    *
    * - Set document title 
    * - Set iframe height if needed
    * - Check if form close date is in the past
    */

    useEffect(() => {

        if (!config) return;
        if (config.h1) document.title = `Formulaire - ${config.h1.replace(/<\/?[^>]+(>|$)/g, "")}`;

        // receive matomo tracking tag from parent frame if needed
        window.addEventListener("message", (e) => {
            if (typeof x !== 'object') return;
            if ('mtm_campaign' in e.data && e.data.mtm_campaign)
                formData['mtm_campaign'] = { value: e.data.mtm_campaign }
        });

        // check if form close date is in the past
        setFormClosed(Date.now() > Date.parse(config.expiration))


        // iframe post height message
        const slrListener = () => {
            const wh = document.querySelector('.App')?.getBoundingClientRect().height;
            window.parent.postMessage({ frameHeight: wh }, '*')
        };

        ['scroll', 'load', 'resize'].forEach(e => {
            window.addEventListener(e, slrListener);
        })
        let postFrameHeightInterval;
        postFrameHeightInterval = setInterval(() => slrListener(), 250);
        return () => {
            ['scroll', 'load', 'resize'].forEach(e => {
                window.removeEventListener(e, slrListener);
                clearInterval(postFrameHeightInterval);
            })
        }

        

    }, [config, formData]);


    /*
     *
     * Debug 
     */

    useEffect(() => {
        if (window.location.hostname === 'localhost')
            console.table(formData);
        else
            window.userInputsFormData = formData;
    }, [formData]);


    /*
     *
     * Input hook 
     */

    useEffect(() => {
        // Run hook if any 
        const _hook = async () => {
            if (_eval(config, 'inputHook')) {
                let hookedConf = await hook(config.inputHook, config, formData);
                setConfig(hookedConf);
            }
        }
        _hook()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData]);

    /*
     *
     * Get form steps (min, max, current)
     */

    const getFormSteps = (conf) => {
        const registeredSteps = conf.inputs.map(i => i.step).filter(i => i !== undefined);
        // if is multi step form
        if (registeredSteps.length > 0) {
            // - get min and max steps
            const min = registeredSteps.reduce((a, b) => Math.min(a, b))
            const max = registeredSteps.reduce((a, b) => Math.max(a, b))
            // - set steps data
            setSteps({ min: min, max: max, current: min })
        }
        // else set default step to 0 foreach inputs
        else for (const i of conf.inputs) i.step = 0;
    }

    const stepChange = (offset, checkValidity = true) => {

        if (window.location.hostname === 'localhost')
            console.log('[stepChange]', formData);

        if(checkValidity) {
            const isValid = checkFormValidity();

            console.log('[stepChange]', 'form isValid > ', isValid);
    
            if (!isValid) return
        }
        setSteps({ ...steps, current: steps.current + offset })
        window.scrollTo(0, document.querySelector('#aefevent-form form').offsetTop)
        setValidated(false);

        // iframe, request scroll top message
        window.parent.postMessage({ scrollTopEvent: true }, '*');
    }

    /*
     *
     * Inputs values change event handler
     */

    const handleChange = (id, v) => {

        let els = document.querySelectorAll(`[data-validity-selector=${id}]`)
        setErrorsBeforeSubmit(false)
        els.forEach(el => {
            if (v.validity) el.setCustomValidity('');
            else el.setCustomValidity(false);
        })

        setFormData(fd => {
            let _formData = { ...fd }
            _formData[id] = v
            return _formData
        })
    }

    /*
     *
     * Send success mail to the user 
     */

    const sendSuccessMail = async (email) => {
        return new Promise((resolve, reject) => {
            config.sendSuccessMail['to'] = email
            const endPoint = window.location.hostname === 'localhost' && 'localApiEndPoint' in config
                ? config.localApiEndPoint
                : config.apiEndPoint

            let data = config.sendSuccessMail;

            axios.post(endPoint, { data: data })
                .catch(function (error) {
                    console.error(error)
                    setErrorModalShow(true)
                    setPendingSend(false)
                })
                .then(res => {
                    if (res && 'status' in res && res.status === 200)
                        console.log('Confirmation email envoyée.')
                    else {
                        console.log(res)
                        throw Error("Error while sending confirmation mail.");
                    }
                })
                .finally(() => resolve())
        });
    }

    /*
     *
     * Send success mail to the admin 
     */

    const sendNotificationMail = async () => {
        return new Promise((resolve, reject) => {
            // Build HTML content
            let inputContent = ''
            Object.entries(formData).forEach(([k, v]) => {
                if (
                    (k !== 'INTERETS')
                    && (k !== 'TOKEN')
                ) {
                    const label = config.inputs.find(i => i.sid === k).label
                    inputContent += `<p><strong>${label}</strong><span> : ${v.value}</span></p></hr>`
                }
            })
            if(inputContent && 'sendNotificationMail' in config && '_HTML_CONTENT' in config.sendNotificationMail) {
                config.sendNotificationMail._HTML_CONTENT = config.sendNotificationMail._HTML_CONTENT.replace(/{{ FORM_FIELDS }}/g, inputContent)
            }

            // Send mails
            const endPoint = window.location.hostname === 'localhost' && 'localApiEndPoint' in config
                ? config.localApiEndPoint
                : config.apiEndPoint

            function doApiCall(data) {
                return new Promise((r) => {
                    axios.post(endPoint, { data: data })
                        .catch(function (error) {
                            console.error(error)
                        })
                        .then(res => {
                            if (res && 'status' in res && res.status === 200)
                                console.log('Notification par email envoyée.')
                            else {
                                console.log(res)
                                throw Error("Error while sending notification mail.");
                            }
                        })
                        .finally(() => r())
                });
            }

            console.log(config.sendNotificationMail)
            const promises = config.sendNotificationMail['to'].map(to => {
                let data = structuredClone(config.sendNotificationMail);
                console.log(data)
                data['to'] = to
                return doApiCall(data)
            });

            Promise.all(promises).then(() => resolve())

        });
    }


    /*
     *
     * Allow multiple values for INTERETS field (merge values with pipe) 
     */
    
    const mergeInterests = (key, value) => {
        if (key === 'INTERETS' && formData[key]?.value)
            return { value: formData[key].value + ' | ' + value };
        else
            return { value: value };
    }

    /*
     *
     * Prepare token and store hidden fields in the formData object
     */

    const prepareHiddenFields = () => {

        // Hidden fields
        config.inputs.filter(i => i.type === "hidden").forEach(i => {
            // Token field: generate token from fields
            if (i.sid === "TOKEN") {
                formData["TOKEN"] = {}
                if (formData["PRENOM"] && formData["NOM"] && formData["EMAIL"]) {
                    const contactFonction = (formData["FONCTION"] && formData["FONCTION"].value) ? formData["FONCTION"].value : '';
                    let contactSociete = (formData["ORGANISATION"] && formData["ORGANISATION"].value) ? formData["ORGANISATION"].value : '';
                    if (formData["SOCIETE"] && formData["SOCIETE"].value) contactSociete = formData["SOCIETE"].value;
                    formData["TOKEN"].value = window.btoa(
                        unescape(
                            encodeURIComponent(
                                JSON.stringify({
                                    p: formData["PRENOM"].value,
                                    n: formData["NOM"].value,
                                    e: formData["EMAIL"].value,
                                    f: contactFonction,
                                    c: contactSociete
                                })
                            )
                        )
                    )
                }
            } else {
                // Other hidden field

                // if has condition
                if (_eval(i, 'if')) {
                    if (
                        formData[i.if.field]
                        && (formData[i.if.field].value === i.if.is)
                    )
                        formData[i.sid] = mergeInterests(i.sid, i.value)
                }
                // else: take the value from the config file
                else formData[i.sid] = mergeInterests(i.sid, i.value)
            }
        })
    }


    /*
     *
     * Submit form logic 
     */

    const handleSubmit = (e) => {

        e.preventDefault();
        e.stopPropagation();

        const isValid = checkFormValidity();
        if (isValid) {
            sendForm();
        } 
    }


    /*
     *
     * Check form validity 
     */
    
    const checkFormValidity = () => {

        let isValid = true;

        // Ensure RGPD checkbox is checked if last step
        if(steps.current === steps.max )
        {
            const rgpdCheckbox = document.querySelector('#checkbox-rgpd');
            if (rgpdCheckbox && !rgpdCheckbox.checked)
                isValid = false;
            else if (rgpdCheckbox)
                rgpdCheckbox.setCustomValidity('');
        }
        
        // Set fields validity based on child component validity feedback 
        document.querySelectorAll('[data-validity-selector]')
            .forEach(el => {
                const sid = el.getAttribute('data-validity-selector')

                // If field step doesn't match current step, don't check validity
                if (config.inputs.find(i => i.sid === sid).step !== steps.current) {
                    el.setCustomValidity('');
                }

                // If field is not required, don't check validity
                else if (config.inputs.find(i => i.sid === sid).required !== true){
                    el.setCustomValidity(''); 
                }

                // Check validity based on component feedback
                else if (formData[sid]?.validity){
                    el.setCustomValidity('');
                }

                else {
                    console.log('[checkFormValidity]', 'field doesn t pass validity checks > ', sid)
                    el.setCustomValidity(false);
                    isValid = false;
                }
            })

        // Prepare token and store hidden fields in the formData object
        prepareHiddenFields();

        // mark that form validation check has been done
        setValidated(true);

        // if form is not valid, show error message
        if(!isValid) {
            setErrorsBeforeSubmit(true)
            window.scrollTo(0, document.querySelector('form').offsetTop)
        }

        return isValid;
    }

    /*
     *
     * Send form data 
     */

    const sendForm = async () => {

        setPendingSend(true)

        // Map fields (replace or fill the mapped field value before sent)
        for (const [key, value] of Object.entries(formData)) {
            if (typeof value === 'object' && value !== null)
                // Special case for INTERETS field  (if is maped to INTERETS field, merge it)
                if (value.map === 'INTERETS') {
                    formData['INTERETS'] = mergeInterests('INTERETS', value.value);
                }   
                if (('map' in value) && (value.map !== false) && value.map !== 'INTERETS') {
                    formData[formData[key].map].value = formData[formData[key].map].value + ' -> ' + value.value;
                }
        }

        // clean up dupplicates interests
        if (formData['INTERETS']?.value) {
            const interests = formData['INTERETS'].value.split(' | ');
            const uniqueInterests = [...new Set(interests)];
            formData['INTERETS'].value = uniqueInterests.join(' | ');
        }

        // clean up the data object and send it
        const data = (() => {
            var out = {}
            // regular form data
            for (const [key, value] of Object.entries(formData)) {
                out[key] = value.value
            }
            // meta data
            out['_LIST_ID'] = config.inputs.find(i => i.sid = '_LIST_ID').value;

            return out
        })();

        // retrieve endpoint
        const endPoint = window.location.hostname === 'localhost' && 'localApiEndPoint' in config
            ? config.localApiEndPoint
            : config.apiEndPoint

        // extra params to render in the confirm email template
        data['_MAIL_TEMPLATE_PARAMS'] = config.sendSuccessMail?._MAIL_TEMPLATE_PARAMS || {};

        // send data
        axios.post(endPoint, { data })
            .catch(function (error) {
                console.error(error)
                setErrorModalShow(true)
                setPendingSend(false)
            })
            .then(res => {
                if (res && 'status' in res && res.status === 200) {
                    sendFormSuccess(config, data);
                }
                else setErrorModalShow(true)
                setPendingSend(false)
            })
    }

    /*
     *
     * When form is sent successfully 
     */
    
    const sendFormSuccess = async (conf, data) => {

        // Run hook if any
        if (_eval(conf, 'formSentSuccessHook')) {
            conf = await hook(conf.formSentSuccessHook, config, data);
        }
        // send success mail to the user        
        if (conf.sendSuccessMail?.send) {
            await sendSuccessMail(data['EMAIL'])
        }
        // send notification mail to the admin
        if (conf.sendNotificationMail?.send) {
            await sendNotificationMail()
        }
        // redirect to a page
        if (isUrl(conf.redirectUrlAfterSubmit)) {
            window.parent.postMessage({ redirect: conf.redirectUrlAfterSubmit }, '*')
            // if (conf.enableDecoration === true) // ??
            window.location.href = conf.redirectUrlAfterSubmit;
        }
        // display success message
        else
            setSuccessModalShow(true)
        // reload form if needed
        if (conf.loopable)
            setTimeout(() => { window.location.reload() }, 1500)
    };



    /*
     *
     * File upload 
     */

    const fileUpload = async (sid, file) => {

        let data = new FormData();
        data.append('file', file);

        const endPoint = window.location.hostname === 'localhost' && 'localApiEndPoint' in config
            ? config.localApiEndPoint
            : config.apiEndPoint

        return await axios.post(
            endPoint,
            data, {
            headers: {
                "Content-Type": "multipart/form-data",
            },
        })
            .catch(function (error) {
                console.error(error)
                setErrorModalShow(true)
                setPendingSend(false)
                return false;
            })
            .then(res => {
                if (res && 'status' in res && res.status === 200) {
                    if (isUrl(res.data.file_url)) {
                        return res.data.file_url;
                    }
                }
                else setErrorModalShow(true)
                setPendingSend(false)
            })
    }



    if (!ready || !config)
        return (<>{/* Chargement... */}</>)
    else
        return (
            <>
                {(!process.env.NODE_ENV || process.env.NODE_ENV === 'development')
                    && Object.keys(_config).length === 0
                    ?
                    <Container>
                        <a href="/form/v2/k845kdka" className="d-none d-lg-block position-absolute top-0 left-0 text-white py-4">
                            <span className="material-symbols-outlined">arrow_back</span>
                            <small>Liste des formulaires</small></a>
                    </Container>
                    : null}


                <div id="aefevent-form" className={`App${config.enableDecoration === false || config.enableHeader === false ? "" : " decoration"}`}>
                    <style>
                        :root{'{\n' + Object.entries(config.colors).map(([k, v]) => `--color-${k}: ${v};`).join('\n') + '\n}'}
                    </style>
                    <Container fluid className='bg-light p-0'>
                        <Container id="heading-bg" fluid style={{ background: config.colors.primary }}> </Container>

                        <Container id="content" className={pendingSend ? 'pending' : ''}>
                            {config.enableHeader !== false && config.enableBanner && config.bannerLink && config.bannerFileName ?
                                <a href={config.bannerLink} target="_blank" rel="noreferrer" className="position-relative d-block">
                                    <img style={{ width: '700px' }} src={`${process.env.PUBLIC_URL}/banner/${config.bannerFileName || confFile + '.jpg'}`} id="banner" alt="banner" />
                                </a>
                                : null}
                            {config.enableHeader !== false && config.enableBanner && !config.bannerLink && config.bannerFileName ?
                                <img style={{ width: '700px' }} src={`${process.env.PUBLIC_URL}/banner/${config.bannerFileName || confFile + '.jpg'}`} id="banner" alt="banner" />
                                : null}

                            {config.enableHeader !== false && (config.h1?.length > 0 || config.h2?.length > 0 || config.descr?.length > 0) ?
                                <Row>
                                    <Col>
                                        <h1 className='fs-3 fw-bold' style={{ color: config.colors.primary }} dangerouslySetInnerHTML={{ __html: config.h1 }}></h1>
                                        <h2 className='fs-5 fw-bold' style={{ color: config.colors.secondary }} dangerouslySetInnerHTML={{ __html: config.h2 }}></h2>
                                        <p dangerouslySetInnerHTML={{ __html: config.descr }}></p>
                                        <hr />
                                    </Col>
                                </Row>
                                : null}
                            {!formClosed ?
                                <Form noValidate validated={validated}>
                                    {errorsBeforeSubmit ?
                                        <Row>
                                            <Col>
                                                <div className='alert alert-danger mt-3'>Des erreurs sont présentes dans le formulaire.</div>
                                            </Col>
                                        </Row>
                                        : null}
                                    <Row className={pendingHooks ? 'fields pending' : 'fields'}>
                                        {
                                            config.inputs.map((i, _i) => {
                                                return (
                                                    <Form.Group as={Col} sm={i.column ? i.column : "12"} key={_i}
                                                        className={(!(isNaN(i.step) || i.step === steps.current)) ? 'd-none' : ''}
                                                    >
                                                        {
                                                            {
                                                                'text':
                                                                    <TextInput
                                                                        data={i}
                                                                        handleChange={handleChange}
                                                                        formData={formData} />,
                                                                'textarea':
                                                                    <TextareaInput
                                                                        data={i}
                                                                        handleChange={handleChange}
                                                                        formData={formData} />,
                                                                'date':
                                                                    <DateInput
                                                                        data={i}
                                                                        handleChange={handleChange}
                                                                        formData={formData} />,
                                                                'checkbox':
                                                                    <CheckboxInput
                                                                        data={i}
                                                                        handleChange={handleChange}
                                                                        formData={formData} />,
                                                                'checkboxes':
                                                                    <CheckboxesInput
                                                                        data={i}
                                                                        handleChange={handleChange}
                                                                        formData={formData} />,
                                                                'select':
                                                                    <SelectInput
                                                                        data={i}
                                                                        handleChange={handleChange}
                                                                        formData={formData} />,
                                                                'radio':
                                                                    <RadioInput
                                                                        data={i}
                                                                        handleChange={handleChange}
                                                                        formData={formData} />,
                                                                'file':
                                                                    <FileInput
                                                                        data={i}
                                                                        handleChange={handleChange}
                                                                        handleFileUpload={fileUpload}
                                                                        formData={formData} />,
                                                            }[i.type]
                                                        }
                                                    </Form.Group>
                                                )
                                            })
                                        }

                                        {/* RGPD checkbox */}
                                        {config.rgpdCheckboxLabel && steps.current === steps.max ?
                                            <Col xs={12}>
                                                <Form.Check
                                                    id='checkbox-rgpd'
                                                    className='mt-4'
                                                    feedback="Vous devez accepter les conditions pour pouvoir continuer."
                                                    feedbackType="invalid"
                                                >
                                                    <Form.Check.Input required type='checkbox' />
                                                    <Form.Check.Label dangerouslySetInnerHTML={{ __html: config.rgpdCheckboxLabel + "<sup class='text-danger'>*</sup>" }}></Form.Check.Label>
                                                </Form.Check>
                                            </Col>
                                            : null}

                                        <Col xs={12}>
                                            <Row className="mt-4">

                                                {/* Prev step buttons */}
                                                <Col xs={6} className={steps.current !== steps.min ? 'd-block' : 'd-none'}>
                                                    <Button
                                                        style={{ background: '#eee', borderColor: '#d8d8d8', color: '#626262' }}
                                                        onClick={e => stepChange(-1, false)}
                                                        >
                                                        Précédent
                                                    </Button>
                                                </Col>

                                                {/* Next step buttons */}
                                                <Col xs={steps.current === steps.min ? 12 : 6} className={steps.current !== steps.max ? 'd-flex justify-content-end' : 'd-none'}>
                                                    <Button
                                                        style={{ background: config.colors.primary, borderColor: config.colors.primary }}
                                                        onClick={e => stepChange(1)}
                                                    >
                                                        Suivant
                                                    </Button>
                                                </Col>

                                                {/* Submit button */}
                                                <Col xs={steps.min === steps.max ? 12 : 6} className={steps.current === steps.max ? 'd-flex justify-content-end' : 'd-none'}>
                                                    <Button
                                                        disabled={pendingSend}
                                                        type="submit"
                                                        className={steps.min === steps.max ? 'w-100' : ''}
                                                        style={{ background: config.colors.primary, borderColor: config.colors.primary }}
                                                        onClick={handleSubmit}
                                                    >
                                                        {pendingSend ?
                                                            <Spinner
                                                                className='me-2'
                                                                as="span"
                                                                animation="border"
                                                                size="sm"
                                                                role="status"
                                                                aria-hidden="true"
                                                            />
                                                            : null}
                                                        {config.sendButtonLabel}
                                                    </Button>
                                                </Col>

                                            </Row>
                                        </Col>

                                    </Row >
                                </Form>
                                :
                                <Row>
                                    <Col>
                                        <p className="alert alert-danger text-center" dangerouslySetInnerHTML={{ __html: config.expirationMessage }}></p>
                                    </Col>
                                </Row>
                            }
                            {config.htmlAfterForm ?
                                <Row>
                                    <Col>
                                        <div dangerouslySetInnerHTML={{ __html: config.htmlAfterForm }} />
                                    </Col>
                                </Row>
                                : null}

                            {config.footerBannerFileName && config.footerBannerFileName.length > 0 ?
                                <img style={{ width: '700px' }} className='mt-4 w-100' src={`${process.env.PUBLIC_URL}/banner/${config.footerBannerFileName || confFile + '.jpg'}`} alt="banner" />
                                : null}
                        </Container>

                        {config.legalNotice && config.legalNotice.length > 0 ?
                            <Container className='pt-5 pb-2'>
                                <Row>
                                    <p className='text-muted' dangerouslySetInnerHTML={{ __html: config.legalNotice }} />
                                </Row>
                            </Container>
                            : null}
                    </Container>


                    <SuccessModal
                        show={successModalShow}
                        onHide={() => { setSuccessModalShow(false); if (window.location.hostname !== 'localhost') window.location.reload() }}
                        config={config}
                        formdata={formData}
                    />

                    <ErrorModal
                        show={errorModalShow}
                        onHide={() => setErrorModalShow(false)}
                    />
                </div>
            </>
        );
}

export default App;
